// src/pages/VocabularyPage.jsx - UPDATED WITH ANKI-STYLE SRS
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, BarChart3, BookOpen, GraduationCap, ChevronLeft, Pencil, Plus, Save, Trash2, HelpCircle, Sparkles, Brain, Play, Home, User, LayoutGrid, RotateCw, Volume2 } from 'lucide-react';
import { auth, db } from '../../firebase.js';
import { collection, addDoc,updateDoc, increment, getDocs, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { logVocabularyLearning } from '../../utils/activityLogger.js';
import { trackPaywallShown } from '../../utils/conversionEvents.js';
import AhaUpsellModal from '../ui/AhaUpsellModal.jsx';
import Confetti from 'react-confetti';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import subscriptionService from '../../services/subscriptionService';
// Import new components
import { AnkiSRS, CardState, Rating,  } from '../../services/srsService.js';
import VocabularyCard from './VocabularyCard.jsx';
import ReviewButtons from './ReviewButtons.jsx';
import { SwipeHint } from './SwipeIndicators.jsx';
import VocabularyGuide from './VocabularyGuide.jsx';
import GrammarBottomNav from '../grammar/GrammarBottomNav.jsx';
// Initialize SRS service
const srsService = new AnkiSRS();

// Sound hook
const useSound = (soundUrl, volume = 0.5) => {
  const playSound = useCallback(() => {
    try {
      const audio = new Audio(soundUrl);
      audio.volume = volume;
      audio.play().catch(e => console.error("Error playing sound:", e));
    } catch (error) {
      console.error("Could not play sound:", error);
    }
  }, [soundUrl, volume]);
  return playSound;
};

// Load vocabulary data from JSON
const loadVocabularyData = async (level, lang) => {
  const defaultLang = 'en';
  const levelLower = level.toLowerCase();

  // 1. First, try to fetch the language-specific file (e.g., vocabulary-a1_fr.json)
  try {
    const response = await fetch(`/data/vocabulary/vocabulary-${levelLower}_${lang}.json`);
    if (!response.ok) {
      // If the file exists but there's an error, throw to trigger the catch block
      throw new Error(`File not found or error loading for lang: ${lang}`);
    }
    const data = await response.json();
    console.log(`Successfully loaded ${lang} vocabulary for level ${level}`);
    return data;
  } catch (error) {
    console.warn(`Could not load '${lang}' vocabulary for ${level}. Falling back to English.`, error);

    // 2. If the first fetch fails, fall back to the default English file
    try {
      const response = await fetch(`/data/vocabulary/vocabulary-${levelLower}_${defaultLang}.json`);
      if (!response.ok) {
        throw new Error('Failed to load default English vocabulary data');
      }
      const data = await response.json();
      console.log(`Loaded fallback ${defaultLang} vocabulary for level ${level}`);
      return data;
    } catch (fallbackError) {
      console.error('CRITICAL: Could not load even the fallback English vocabulary file.', fallbackError);
      return {}; // Return empty object on complete failure
    }
  }
};

// This function below remains almost the same, we just need to pass the language code through it.
const parseVocabularyData = (data) => {
    const topics = {};
    if (!data || !data.categories) {
        return topics;
    }
    for (const [categoryKey, category] of Object.entries(data.categories)) {
      topics[categoryKey] = {
        id: categoryKey,
        name: category.name,
        icon: category.icon || '📚',
        isOfficial: true,
        isCategory: true,
        subcategories: {}
      };

      for (const [subcatKey, subcat] of Object.entries(category.subcategories || {})) {
        const fullKey = `${categoryKey}_${subcatKey}`;
        topics[fullKey] = {
          id: fullKey,
          name: subcat.name,
          icon: subcat.icon || category.icon || '📚',
          cards: subcat.cards || [],
          isOfficial: true,
          isCategory: false,
          parentCategory: categoryKey
        };

        topics[categoryKey].subcategories[fullKey] = subcat.name;
      }
    }
    return topics;
};

// Load user's custom decks
const loadUserDecks = async () => {
  const userId = auth.currentUser?.uid;
  if (!userId) return {};

  try {
    const decksRef = collection(db, `users/${userId}/vocabulary_decks`);
    const querySnapshot = await getDocs(decksRef);
    const userDecks = {};

    querySnapshot.forEach((doc) => {
      const deckData = doc.data();
      userDecks[doc.id] = {
        id: doc.id,
        name: deckData.name,
        icon: deckData.icon || '✏️',
        cards: deckData.cards || [],
        isOfficial: false,
        isCategory: false,
        createdAt: deckData.createdAt
      };
    });

    return userDecks;
  } catch (error) {
    console.error("Error loading user decks:", error);
    return {};
  }
};

// Load SRS data with migration support
const loadSRSData = async (subcategoryId) => {
  const userId = auth.currentUser?.uid;
  if (!userId) return {};

  try {
    const srsRef = doc(db, `users/${userId}/srs/${subcategoryId}`);
    const srsDoc = await getDoc(srsRef);

    if (srsDoc.exists()) {
      return srsDoc.data();
    }

    return {};
  } catch (error) {
    console.error('Error loading SRS data:', error);
    return {};
  }
};
// Save SRS data
const saveSRSData = async (subcategoryId, cardId, updatedCard) => {
  const userId = auth.currentUser?.uid;
  if (!userId) return;

  try {
    const srsRef = doc(db, `users/${userId}/srs/${subcategoryId}`);
    const srsDoc = await getDoc(srsRef);
    const currentData = srsDoc.exists() ? srsDoc.data() : {};

    await setDoc(srsRef, {
      ...currentData,
      [cardId]: updatedCard,
      lastUpdated: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error saving SRS data:', error);
  }
};

// Session Statistics Component
const SessionStats = ({ stats, onContinue }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-2xl shadow-xl">
      <h2 className="text-2xl font-bold text-center mb-6">Sitzung abgeschlossen! 🎉</h2>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-blue-600">{stats.studied}</div>
         <div className="text-sm text-gray-600">{t('vocabulary.session.cardsStudied')}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-green-600">
            {Math.round((stats.good + stats.easy) / Math.max(stats.studied, 1) * 100)}%
          </div>
          <div className="text-sm text-gray-600">Erfolgsrate</div>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Nochmal:</span>
          <span className="font-semibold text-red-600">{stats.again}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Schwer:</span>
          <span className="font-semibold text-orange-600">{stats.hard}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Gut:</span>
          <span className="font-semibold text-green-600">{stats.good}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Einfach:</span>
          <span className="font-semibold text-blue-600">{stats.easy}</span>
        </div>
      </div>

      <button
        onClick={onContinue}
        className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
      >
        Weiter üben
      </button>
    </div>
  );
};
const DeckEditor = ({ onSave, onCancel, initialData = null }) => {
   const { t } = useTranslation();
  const [deckName, setDeckName] = useState(initialData?.name || '');
  const [deckIcon, setDeckIcon] = useState(initialData?.icon || '📚');
  const [cards, setCards] = useState(initialData?.cards || [
    { front: '', back: '', examples: [{ german: '', english: '' }] }
  ]);

  const addCard = () => {
    setCards([...cards, { front: '', back: '', examples: [{ german: '', english: '' }] }]);
  };

  const removeCard = (index) => {
    setCards(cards.filter((_, i) => i !== index));
  };

  const updateCard = (index, field, value) => {
    const newCards = [...cards];
    newCards[index][field] = value;
    setCards(newCards);
  };

  const addExample = (cardIndex) => {
    const newCards = [...cards];
    newCards[cardIndex].examples.push({ german: '', english: '' });
    setCards(newCards);
  };

  const updateExample = (cardIndex, exampleIndex, field, value) => {
    const newCards = [...cards];
    newCards[cardIndex].examples[exampleIndex][field] = value;
    setCards(newCards);
  };

  const handleSave = () => {
    if (!deckName.trim()) {
      alert('Bitte geben Sie einen Deck-Namen ein');
      return;
    }

    const validCards = cards.filter(card => card.front.trim() && card.back.trim());
if (validCards.length === 0) {
  alert(t('vocabulary.deck.addAtLeastOneCard'));
  return;
}

    // Extract article from front if present
    const processedCards = validCards.map(card => {
      const firstWord = card.front.split(' ')[0].toLowerCase();
      const articles = ['der', 'die', 'das'];

      return {
        ...card,
        article: articles.includes(firstWord) ? firstWord : null
      };
    });

    onSave({
      name: deckName,
      icon: deckIcon,
      cards: processedCards
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-2xl font-bold mb-6">
         {initialData ? t('vocabulary.deck.edit') : t('vocabulary.deck.create')}
        </h2>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deck Name
            </label>
            <input
              type="text"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Meine Vokabeln"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon
            </label>
            <input
              type="text"
              value={deckIcon}
              onChange={(e) => setDeckIcon(e.target.value)}
              className="w-32 px-4 py-2 border border-gray-300 rounded-lg text-2xl"
              placeholder="📚"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-lg">{t('vocabulary.deck.cards')}</h3>
          {cards.map((card, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <span className="font-medium">
  {t('vocabulary.flashcard.cardNumber', { number: index + 1 })}
</span>
                <button
                  onClick={() => removeCard(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {t('vocabulary.card.front')}
                  </label>
                  <input
                    type="text"
                    value={card.front}
                    onChange={(e) => updateCard(index, 'front', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder={t('vocabulary.card.frontPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {t('vocabulary.card.back')}
                  </label>
                  <input
                    type="text"
                    value={card.back}
                    onChange={(e) => updateCard(index, 'back', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder={t('vocabulary.card.backPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Beispiele</label>
                {card.examples.map((example, exIndex) => (
                  <div key={exIndex} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={example.german}
                      onChange={(e) => updateExample(index, exIndex, 'german', e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                      placeholder={t('vocabulary.card.germanExample')}
                    />
                    <input
                      type="text"
                      value={example.english}
                      onChange={(e) => updateExample(index, exIndex, 'english', e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                      placeholder={t('vocabulary.card.englishTranslation')}
                    />
                  </div>
                ))}
                <button
                  onClick={() => addExample(index)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + {t('vocabulary.card.addExample')}
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addCard}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700"
          >
            <Plus className="w-5 h-5 inline mr-2" />
            {t('vocabulary.card.addCard')}
          </button>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('vocabulary.deck.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-5 h-5 inline mr-2" />
            {t('vocabulary.deck.save')}
          </button>
        </div>
      </div>
    </div>
  );
};
// Main VocabularyPage Component
const VocabularyPage = ({ onBack, userLevel = 'B1' }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  // Sound effects
  const playCorrectSound = useSound('/sfx/correct.mp3');
  const playIncorrectSound = useSound('/sfx/incorrect.mp3');
  const [showDeckEditor, setShowDeckEditor] = useState(false);
const [editingDeck, setEditingDeck] = useState(null);
  // State management
  const [currentView, setCurrentView] = useState('level-overview');
  const [practiceLevel, setPracticeLevel] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [currentCards, setCurrentCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [sessionStats, setSessionStats] = useState({
    studied: 0,
    again: 0,
    hard: 0,
    good: 0,
    easy: 0
  });
  const [allTopics, setAllTopics] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [learnedWords, setLearnedWords] = useState([]);
  const [srsData, setSrsData] = useState({});
  const [sessionComplete, setSessionComplete] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);
  const [isBackHovered_Overview, setIsBackHovered_Overview] = useState(false);
  const [isBackHovered_Categories, setIsBackHovered_Categories] = useState(false);
  const [isBackHovered_Subcategories, setIsBackHovered_Subcategories] = useState(false);
  const [isBackHovered_Practice, setIsBackHovered_Practice] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useLocalizedNavigate();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedCategoryName, setBlockedCategoryName] = useState(null);
  const [pendingExitNav, setPendingExitNav] = useState(null);

  const handleAhaDismiss = useCallback(() => {
    setShowPremiumModal(false);
    if (pendingExitNav) {
      const fn = pendingExitNav;
      setPendingExitNav(null);
      fn();
    }
  }, [pendingExitNav]);

  const handleAhaUpgrade = useCallback(() => {
    setPendingExitNav(null);
    setShowPremiumModal(false);
  }, []);

  // Load all data
  const loadAllData = async (level, lang) => {
    setIsLoading(true);
    try {
      const officialData = await loadVocabularyData(level, lang);
      const officialTopics = parseVocabularyData(officialData); // Use the new parser
      const userDecks = await loadUserDecks();
      setAllTopics({ ...officialTopics, ...userDecks });
    } catch (error) {
      console.error('Failed to load vocabulary data:', error);
      setAllTopics({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const guideSeenKey = 'vocabularyGuideCompleted';
    const hasSeenBefore = localStorage.getItem(guideSeenKey) === 'true';
    setHasSeenGuide(hasSeenBefore);
  }, []);
  
  useEffect(() => {
    const loadSubscription = async () => {
      if (auth.currentUser) {
        try {
          const status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error loading subscription status:', error);
          setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        }
      } else {
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      }
    };
    loadSubscription();
  }, []);
  
  // Auto-select level when userLevel prop is provided (e.g., from study plan)
  useEffect(() => {
    if (userLevel && !practiceLevel && currentView === 'level-overview') {
      console.log('🎯 Auto-selecting vocabulary level from prop:', userLevel);
      setPracticeLevel(userLevel);
      setCurrentView('categories');
    }
  }, [userLevel, practiceLevel, currentView]);

  useEffect(() => {
    if (practiceLevel) {
      loadAllData(practiceLevel, currentLanguage); // Pass the current language
    }
  }, [practiceLevel, currentLanguage]);

  // Tab title: category/subcategory name without leading numbers (e.g. "1.0 Personen" → "Personen", "2.3.4 Topic" → "Topic").
  const tabTitleName = (raw) => (raw || '').replace(/^\d+(\.\d+)*\.?\s*/, '').trim() || raw;
  useEffect(() => {
    const rawName = (selectedSubcategory && allTopics[selectedSubcategory]?.name) || (selectedCategory && allTopics[selectedCategory]?.name);
    const name = tabTitleName(rawName);
    const prev = document.title;
    document.title = practiceLevel && name ? `${name} | Vocabulary | Logami` : `${t('vocabulary.title', { defaultValue: 'Vocabulary' })} | Logami`;
    return () => { document.title = prev; };
  }, [practiceLevel, selectedCategory, selectedSubcategory, allTopics, t]);

  // Handle level selection
  const handleLevelSelect = (level) => {
    setPracticeLevel(level);
    setCurrentView('categories');
  };
  const handleGuideComplete = (dontShowAgain = false) => {
    if (dontShowAgain) {
      localStorage.setItem('vocabularyGuideCompleted', 'true');
      setHasSeenGuide(true);
    }
    setShowGuide(false);
    if (selectedCategory && !selectedSubcategory) {
      setCurrentView('subcategories');
    } else if (selectedSubcategory) {
      handleSubcategorySelect(selectedSubcategory);
    }
  };

  // Manual guide trigger
  const showGuideManually = () => {
    setShowGuide(true);
  };
  // Handle category selection
  const handleCategorySelect = (categoryKey) => {
    const category = allTopics[categoryKey];
    setSelectedCategory(categoryKey);

    if (category.isCategory) {
      setCurrentView('subcategories');
    } else {
      handleSubcategorySelect(categoryKey);
    }
  };

  // Handle subcategory selection and prepare cards
  const handleSubcategorySelect = async (subcategoryKey) => {
    // This function can be re-triggered by handleGuideComplete, so store the key
    setSelectedSubcategory(subcategoryKey);

    const subcategory = allTopics[subcategoryKey];
    if (!subcategory || !subcategory.cards || subcategory.cards.length === 0) {
  alert(t('vocabulary.deck.noCardsInDeck'));
  return;
}

    // Load SRS data for this subcategory
    const loadedSrsData = await loadSRSData(subcategoryKey);
    setSrsData(loadedSrsData);

    // Prepare cards with SRS data
    const preparedCards = subcategory.cards.map((card, index) => {
      const cardId = card.id || `${subcategoryKey}-${index}`;
      const existingSrs = loadedSrsData[cardId];

      // Initialize or use existing SRS data
      const srsCard = existingSrs || srsService.initializeCard(cardId);

      return {
        ...card,
        ...srsCard,
        id: cardId,
        front: card.front || card.word || card.german || '',
        back: card.back || card.translation || card.english || '',
        examples: card.examples || [],
        icon: subcategory.icon,
        article: card.article
      };
    });

    // Sort cards by SRS priority and filter due cards
    const dueCards = srsService.getDueCards(preparedCards);
    const sortedCards = srsService.sortByPriority(dueCards);

    if (sortedCards.length === 0) {
      // Calculate when next card will be available
      const nextCard = preparedCards
        .filter(c => c.nextReview)
        .sort((a, b) => new Date(a.nextReview) - new Date(b.nextReview))[0];

     if (nextCard) {
  const timeUntil = new Date(nextCard.nextReview) - new Date();
  const hours = Math.floor(timeUntil / 3600000);
  const minutes = Math.floor((timeUntil % 3600000) / 60000);
  // The 'hours' and 'minutes' are passed as variables for interpolation
  alert(t('vocabulary.deck.noCardsDue', { hours, minutes }));
} else {
  alert(t('vocabulary.deck.allCardsLearned'));
}
return;
    }

    setCurrentCards(sortedCards);
    setCurrentCardIndex(0);
    setCurrentCard(sortedCards[0]);
    setSessionStats({ studied: 0, again: 0, hard: 0, good: 0, easy: 0 });
    setLearnedWords([]);
    setSelectedSubcategory(subcategoryKey);
    setCurrentView('practice');
    setSessionComplete(false);
    setIsCardFlipped(false);
  };

  // Handle card rating (Anki-style)
  const handleCardRating = async (rating) => {
    const card = currentCards[currentCardIndex];
    if (!card) return;

    // Play sound based on rating
    if (rating === Rating.AGAIN) {
      playIncorrectSound();
    } else {
      playCorrectSound();
    }

    // Update card with SRS algorithm
    const updatedCard = srsService.calculateNextReview(card, rating);

    // Save to Firebase
    await saveSRSData(selectedSubcategory, card.id, updatedCard);
    const wasNotMastered = !card.state || card.state !== 'review' || card.interval < 21;
  const isNowMastered = updatedCard.state === 'review' && updatedCard.interval >= 21;

  if (wasNotMastered && isNowMastered) {
    // Card just became mastered - update stats
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          'stats.vocabularyMastered': increment(1),
          'stats.vocabularyWordsLearned': increment(1),
          'stats.lastStudyDate': new Date()
        });
      }
    } catch (error) {
      console.error('Error updating vocabulary stats:', error);
    }
  } else if (rating >= Rating.GOOD && !card.state) {
    // First time learning this card
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          'stats.vocabularyWordsLearned': increment(1),
          'stats.lastStudyDate': new Date()
        });
      }
    } catch (error) {
      console.error('Error updating vocabulary stats:', error);
    }
  }
    if (updatedCard.state === 'review' && updatedCard.interval >= 21) {
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          'stats.vocabularyMastered': increment(1)
        });
      }
    } catch (error) {
      console.error('Error updating vocabulary stats:', error);
    }
  }
    // Track learned words for "Good" and "Easy" ratings
    if (rating >= Rating.GOOD) {
      const wordData = {
        word: card.front,
        translation: card.back,
        examples: card.examples || [],
        category: selectedSubcategory,
        level: practiceLevel || userLevel
      };
      setLearnedWords(prev => [...prev, wordData]);
    }

    // Update session statistics
    setSessionStats(prev => ({
      studied: prev.studied + 1,
      again: prev.again + (rating === Rating.AGAIN ? 1 : 0),
      hard: prev.hard + (rating === Rating.HARD ? 1 : 0),
      good: prev.good + (rating === Rating.GOOD ? 1 : 0),
      easy: prev.easy + (rating === Rating.EASY ? 1 : 0)
    }));

    // Move to next card or complete session
    if (currentCardIndex < currentCards.length - 1) {
  setIsCardFlipped(false); // Reset immediately
  setTimeout(() => {
    setCurrentCardIndex(prev => prev + 1);
    setCurrentCard(currentCards[currentCardIndex + 1]);
  }, 300);
} else {
      // Session complete
      setSessionComplete(true);
      if (learnedWords.length > 0) {
        await logVocabularyLearning(learnedWords);
      }
      setTimeout(() => {
        setCurrentView('stats');
      }, 3000);
    }
  };
  const handleSaveDeck = async (deckData) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    alert('Sie müssen angemeldet sein');
    return;
  }

  try {
    const decksRef = collection(db, `users/${userId}/vocabulary_decks`);

    if (editingDeck) {
      // Update existing deck
      await setDoc(doc(decksRef, editingDeck.id), {
        ...deckData,
        updatedAt: new Date().toISOString()
      });
    } else {
      // Create new deck
      await addDoc(decksRef, {
        ...deckData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }

    alert('Deck erfolgreich gespeichert!');
    setShowDeckEditor(false);
    setEditingDeck(null);
    loadAllData(practiceLevel); // Reload data
  } catch (error) {
    console.error('Error saving deck:', error);
    alert('Fehler beim Speichern des Decks');
  }
};

// Delete custom deck
const handleDeleteDeck = async (deckId) => {
  if (!confirm('Möchten Sie dieses Deck wirklich löschen?')) return;

  const userId = auth.currentUser?.uid;
  if (!userId) return;

  try {
    await deleteDoc(doc(db, `users/${userId}/vocabulary_decks/${deckId}`));
    alert('Deck gelöscht!');
    loadAllData(practiceLevel);
  } catch (error) {
    console.error('Error deleting deck:', error);
    alert('Fehler beim Löschen');
  }
};
  // Handle swipe (maps to Good/Again)
  const handleSwipe = (cardId, direction) => {
  // Map swipe to rating: right = Good, left = Again
  const rating = direction === 'know' ? Rating.GOOD : Rating.AGAIN;
  handleCardRating(rating);
};

  // Render based on current view
  const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';

  const mainContent = (() => {
  switch (currentView) {

    case 'level-overview':
      return (
        <div className="min-h-screen bg-white relative overflow-hidden pb-20 sm:pb-0">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5">
           {/* Compact Header Row */}
           <div className="flex items-start justify-between gap-3 mb-3 sm:mb-5">
             {/* Back Button, hidden on mobile, in bottom nav */}
             <button
               onClick={onBack}
               onMouseEnter={() => setIsBackHovered_Overview(true)}
               onMouseLeave={() => setIsBackHovered_Overview(false)}
               className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
             >
               {/* Wave fill effect (Right-to-Left) */}
               <div
                 style={{
                   position: 'absolute',
                   top: 0,
                   bottom: 0,
                   right: 0,
                   width: isBackHovered_Overview ? '100%' : '0%',
                   background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
                   transition: 'width 0.7s ease-out',
                   zIndex: 0
                 }}
               />
               <ArrowLeft
                 className="w-5 h-5 relative z-10 transition-colors duration-500"
                 style={{ color: isBackHovered_Overview ? '#ffffff' : '#000000' }}
               />
               <span
                 className="font-semibold relative z-10 transition-colors duration-500"
                 style={{ color: isBackHovered_Overview ? '#ffffff' : '#000000' }}
               >
                 {t("grammar.back")}
               </span>
             </button>

             {/* Badge */}
             <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white text-xs sm:text-sm font-bold tracking-widest uppercase rounded-full flex-shrink-0">
               {t('vocabulary.trainer')}
             </div>
           </div>

            {/* Title and Subtitle */}
            <div className="text-center mb-5 sm:mb-8">
              <h1 className="font-space text-[clamp(1.5rem,4vw,2.5rem)] sm:text-[clamp(1.75rem,4vw,3rem)] font-black text-black mb-1.5 sm:mb-3 tracking-tight leading-tight">
                {t('vocabulary.level.selectYourLevel')}
              </h1>
              <p className="text-sm sm:text-base md:text-xl text-black/60 max-w-3xl mx-auto leading-snug">
                Choose your learning path and master German vocabulary
              </p>
            </div>

            {/* Level Cards Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
              {['A1', 'A2', 'B1', 'B2', 'C1'].map((level, index) => (
                <div
                  key={level}
                  onClick={() => handleLevelSelect(level)}
                  className="vocab-level-card p-4 sm:p-8 rounded-3xl border-[3px] border-black bg-white text-black cursor-pointer group touch-manipulation min-h-[44px] min-w-[44px]"
                >
                  {/* Number Badge */}
                  <div className="vocab-number-bg absolute top-4 right-6 font-space text-[4rem] font-black text-[#3A86FF] opacity-15 leading-none">
                    {index + 1}
                  </div>

                  {/* Decorative Dots */}
                  <div className="vocab-decorative-dot vocab-dot-1"></div>
                  <div className="vocab-decorative-dot vocab-dot-2"></div>
                  <div className="vocab-decorative-dot vocab-dot-3"></div>

                  {/* Shine Effect */}
                  <div className="vocab-shine-effect"></div>

                  {/* Icon */}
                  <div className="vocab-icon-animated relative w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] bg-[#3A86FF] rounded-2xl flex items-center justify-center text-white text-3xl mb-6 mx-auto">
                    <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>

                  {/* Level Label */}
                  <h3 className="vocab-title-animated font-space text-[clamp(1.5rem,4vw,2.5rem)] font-black text-[#3A86FF] text-center">
                    {level}
                  </h3>

                  {/* Corner Decorations */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/50 transition-colors"></div>
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/50 transition-colors"></div>
                </div>
              ))}
            </div>

          </div>

          {/* Bottom Navigation. Level Overview */}
          <GrammarBottomNav buttons={[
            { id: 'back', icon: ChevronLeft, label: t('grammar.back'), onClick: onBack, color: '#3A86FF' },
            { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
            { id: 'practice', icon: GraduationCap, label: 'Practice', onClick: () => navigate('/practice'), color: '#3A86FF' },
            { id: 'profile', icon: User, label: 'Profile', onClick: () => navigate('/profile'), color: '#3A86FF' },
          ]} />
        </div>
      );
    case 'categories':
  return (
    <div className="min-h-screen bg-white relative overflow-hidden pb-20 sm:pb-0">
      <div className="relative z-10 max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-2 sm:py-6">
        {/* Mobile: Single compact header line. Desktop: original layout */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-6">
          {/* Back Button, hidden on mobile, in bottom nav */}
          <button
            onClick={() => setCurrentView('level-overview')}
            onMouseEnter={() => setIsBackHovered_Categories(true)}
            onMouseLeave={() => setIsBackHovered_Categories(false)}
            className="hidden sm:inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                width: isBackHovered_Categories ? '100%' : '0%',
                background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
                transition: 'width 0.7s ease-out',
                zIndex: 0
              }}
            />
            <ArrowLeft
              className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-500"
              style={{ color: isBackHovered_Categories ? '#ffffff' : '#000000' }}
            />
            <span
              className="text-sm sm:text-base font-semibold relative z-10 transition-colors duration-500"
              style={{ color: isBackHovered_Categories ? '#ffffff' : '#000000' }}
            >
              {t("grammar.back")}
            </span>
          </button>

          {/* Title inline on mobile */}
          <div className="flex-1 min-w-0 text-center sm:hidden">
            <p className="text-sm font-bold text-black truncate">
              {t('vocabulary.topic.selectTopic')} · <span className="text-[#3A86FF]">{practiceLevel}</span>
            </p>
          </div>

          {/* Badge and Help Button Row */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <div className="hidden sm:inline-block px-4 py-2 bg-black text-white text-sm font-bold tracking-widest uppercase rounded-full">
              VOCABULARY TOPICS
            </div>
            <button
              onClick={showGuideManually}
              className="hidden sm:flex w-12 h-12 bg-white border-[3px] border-black/15 hover:border-[#3A86FF] rounded-full items-center justify-center text-black/60 hover:text-[#3A86FF] transition-all hover:scale-110 hover:shadow-lg group touch-manipulation min-w-[44px] min-h-[44px]"
              title="Show Guide"
            >
              <HelpCircle className="w-6 h-6 group-hover:rotate-12 transition-transform" />
            </button>
          </div>
        </div>

        {/* Desktop-only: Title and Level Row */}
        <div className="hidden sm:block mb-6">
          <h2 className="font-space text-[clamp(1.5rem,4vw,3rem)] font-black text-black mb-2 tracking-tight">
            {t('vocabulary.topic.selectTopic')}
          </h2>
          <p className="text-xl text-black/60">
            {t('vocabulary.level.current')}: <span className="font-bold text-[#3A86FF]">{practiceLevel}</span>
          </p>
        </div>

        {/* Create Custom Deck Button */}
        <button
          onClick={() => setShowDeckEditor(true)}
          className="vocab-create-btn w-full mb-3 sm:mb-8 py-2.5 sm:py-6 border-2 sm:border-[3px] border-dashed border-black/30 rounded-xl sm:rounded-3xl text-black hover:border-[#3A86FF] hover:bg-[#3A86FF]/5 transition-all flex items-center justify-center gap-2 sm:gap-3 group overflow-hidden relative"
        >
          <div className="vocab-create-shine"></div>
          <div className="relative z-10 flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-[#3A86FF] rounded-lg sm:rounded-xl flex items-center justify-center text-white group-hover:scale-110 group-hover:rotate-6 transition-all">
              <Plus className="w-4 h-4 sm:w-6 sm:h-6" />
            </div>
            <span className="font-space font-bold text-sm sm:text-xl">{t('vocabulary.deck.createCustom')}</span>
          </div>
        </button>

        {/* Topics Grid, equal height cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6 auto-rows-fr">
          {Object.entries(allTopics)
            .filter(([, topic]) => topic.isCategory || !topic.parentCategory)
            .map(([key, topic], index) => {
              const isLocked = subscriptionStatus?.isFree && index > 0;
              return (
              <div key={key} className="relative group">
                {/* Official Category Card (White) */}
                {topic.isOfficial ? (
                  <div
                    onClick={() => {
                      if (isLocked) {
                        setBlockedCategoryName(topic.name || key);
                        setShowPremiumModal(true);
                        trackPaywallShown({ context: 'vocabulary' });
                      } else {
                        handleCategorySelect(key);
                      }
                    }}
                    className={`vocab-category-card p-3 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] text-black h-full cursor-pointer touch-manipulation min-h-[44px] min-w-[44px] ${isLocked ? 'border-[#6366f1] bg-gradient-to-br from-white via-[#f8f5ff] to-[#eef4ff] shadow-[0_8px_24px_rgba(99,102,241,0.2)]' : 'border-black bg-white'}`}
                  >
                    {/* Decorative Dots */}
                    <div className="vocab-decorative-dot vocab-dot-1 hidden sm:block"></div>
                    <div className="vocab-decorative-dot vocab-dot-2 hidden sm:block"></div>
                    <div className="vocab-decorative-dot vocab-dot-3 hidden sm:block"></div>

                    {/* Shine Effect */}
                    <div className="vocab-shine-effect"></div>

                    {/* Corner Decorations */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/50 transition-colors hidden sm:block"></div>
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/50 transition-colors hidden sm:block"></div>
                    {isLocked && (
                      <div className="absolute top-3 right-3 z-20 inline-flex items-center rounded-full border border-[#6366f1]/30 bg-white/95 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#4f46e5]">
                        {t('premium.only')}
                      </div>
                    )}

                    {/* Icon */}
                    <div
                      className="vocab-icon-animated w-[40px] h-[40px] sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-3xl mb-2 sm:mb-4 mx-auto"
                      style={{ backgroundColor: isLocked ? '#6366f1' : '#3A86FF' }}
                    >
                      {topic.icon || '📚'}
                    </div>

                    {/* Content */}
                    <div className="vocab-content-animated text-center">
                      <h4 className="font-space font-black text-sm sm:text-[clamp(1rem,3vw,1.25rem)] mb-1 sm:mb-2 line-clamp-2">{topic.name}</h4>
                      <p className="text-sm text-black/60 group-hover:text-white/80 transition-colors">
                        {topic.isCategory
                          ? t('vocabulary.deck.subtopics', { count: Object.keys(topic.subcategories || {}).length })
                          : t('vocabulary.deck.cards', { count: (topic.cards || []).length })}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Custom Deck Card (Black with Purple) */
                  <div
                    onClick={() => handleCategorySelect(key)}
                      className="vocab-custom-card p-3 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-black text-white cursor-pointer relative overflow-hidden touch-manipulation min-h-[44px] min-w-[44px] h-full"
                  >
                    {/* Purple Glow Effect */}
                    <div className="vocab-purple-glow"></div>

                    {/* Decorative Dots (Purple) */}
                    <div className="vocab-custom-dot vocab-custom-dot-1 hidden sm:block"></div>
                    <div className="vocab-custom-dot vocab-custom-dot-2 hidden sm:block"></div>
                    <div className="vocab-custom-dot vocab-custom-dot-3 hidden sm:block"></div>

                    {/* Shine Effect */}
                    <div className="vocab-shine-effect"></div>

                    {/* Corner Decorations */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-white/20 group-hover:border-purple-400 transition-colors hidden sm:block"></div>
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-white/20 group-hover:border-purple-400 transition-colors hidden sm:block"></div>

                    {/* Icon */}
                    <div className="vocab-custom-icon w-[40px] h-[40px] sm:w-16 sm:h-16 bg-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                      {topic.icon || '✏️'}
                    </div>

                    {/* Content */}
                    <div className="relative z-10 text-center">
                      <h4 className="font-space font-black text-sm sm:text-[clamp(1rem,3vw,1.25rem)] mb-1 sm:mb-2 line-clamp-2">{topic.name}</h4>
                      <p className="text-xs sm:text-sm text-white/60 mb-1 sm:mb-2">
                        {t('vocabulary.deck.cards', { count: (topic.cards || []).length })}
                      </p>
                      <span className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-purple-600/30 border border-purple-500/50 rounded-full text-[10px] sm:text-xs font-bold text-purple-300">
                        {t('vocabulary.deck.custom')}
                      </span>
                    </div>

                    {/* Edit/Delete Buttons */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all flex gap-2 z-20">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDeck(topic);
                          setShowDeckEditor(true);
                        }}
                        className="w-10 h-10 bg-white text-black rounded-xl flex items-center justify-center hover:bg-purple-500 hover:text-white transition-all hover:scale-110 shadow-lg"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDeck(key);
                        }}
                        className="w-10 h-10 bg-white text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all hover:scale-110 shadow-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
            })}
        </div>

      </div>

      {/* Deck Editor Modal */}
      {showDeckEditor && (
        <DeckEditor
          onSave={handleSaveDeck}
          onCancel={() => {
            setShowDeckEditor(false);
            setEditingDeck(null);
          }}
          initialData={editingDeck}
        />
      )}

      {/* Vocabulary Guide Modal */}
      {showGuide && (
        <VocabularyGuide
          isOpen={showGuide}
          onClose={handleGuideComplete}
        />
      )}

      {/* Bottom Navigation. Categories */}
      <GrammarBottomNav buttons={[
        { id: 'back', icon: ChevronLeft, label: t('grammar.back'), onClick: () => setCurrentView('level-overview'), color: '#3A86FF' },
        { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
        { id: 'create', icon: Plus, label: t('vocabulary.deck.createCustom', { defaultValue: 'Create' }), onClick: () => setShowDeckEditor(true), active: true, color: '#3A86FF' },
        { id: 'help', icon: HelpCircle, label: 'Guide', onClick: showGuideManually, color: '#3A86FF' },
      ]} />
    </div>
  );

    case 'subcategories':
  return (
    <div className="min-h-screen bg-white relative overflow-hidden pb-20 sm:pb-0">
      <div className="relative z-10 max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-2 sm:py-6">
        {/* Mobile: Single compact header line. Desktop: original layout */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-6">
          {/* Back Button, hidden on mobile, in bottom nav */}
          <button
            onClick={() => setCurrentView('categories')}
            onMouseEnter={() => setIsBackHovered_Subcategories(true)}
            onMouseLeave={() => setIsBackHovered_Subcategories(false)}
            className="hidden sm:inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                width: isBackHovered_Subcategories ? '100%' : '0%',
                background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
                transition: 'width 0.7s ease-out',
                zIndex: 0
              }}
            />
            <ArrowLeft
              className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-500"
              style={{ color: isBackHovered_Subcategories ? '#ffffff' : '#000000' }}
            />
            <span
              className="text-sm sm:text-base font-semibold relative z-10 transition-colors duration-500"
              style={{ color: isBackHovered_Subcategories ? '#ffffff' : '#000000' }}
            >
              {t("grammar.back")}
            </span>
          </button>

          {/* Title inline on mobile */}
          <div className="flex-1 min-w-0 text-center sm:hidden">
            <p className="text-sm font-bold text-black truncate">
              {allTopics[selectedCategory]?.name || t('vocabulary.topic.selectSubtopic')}
            </p>
          </div>

          {/* Badge and Help Button Row */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <div className="hidden sm:inline-block px-4 py-2 bg-black text-white text-sm font-bold tracking-widest uppercase rounded-full">
              {allTopics[selectedCategory]?.name || 'VOCABULARY'}
            </div>
            <button
              onClick={showGuideManually}
              className="hidden sm:flex w-12 h-12 bg-white border-[3px] border-black/15 hover:border-indigo-600 rounded-full items-center justify-center text-black/60 hover:text-indigo-600 transition-all hover:scale-110 hover:shadow-lg group touch-manipulation min-w-[44px] min-h-[44px]"
              title="Show Guide"
            >
              <HelpCircle className="w-6 h-6 group-hover:rotate-12 transition-transform" />
            </button>
          </div>
        </div>

        {/* Desktop-only: Title and Subtitle Row */}
        <div className="hidden sm:block mb-6">
          <h2 className="font-space text-[clamp(1.5rem,4vw,3rem)] font-black text-black mb-2 tracking-tight">
            {t('vocabulary.topic.selectSubtopic')}
          </h2>
          <p className="text-xl text-black/60">
            Choose a specific topic to practice
          </p>
        </div>

        {/* Create Custom Deck Button */}
        <button
          onClick={() => setShowDeckEditor(true)}
          className="vocab-create-btn w-full mb-3 sm:mb-8 py-2.5 sm:py-6 border-2 sm:border-[3px] border-dashed border-black/30 rounded-xl sm:rounded-3xl text-black hover:border-[#3A86FF] hover:bg-[#3A86FF]/5 transition-all flex items-center justify-center gap-2 sm:gap-3 group overflow-hidden relative"
        >
          <div className="vocab-create-shine"></div>
          <div className="relative z-10 flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-[#3A86FF] rounded-lg sm:rounded-xl flex items-center justify-center text-white group-hover:scale-110 group-hover:rotate-6 transition-all">
              <Plus className="w-4 h-4 sm:w-6 sm:h-6" />
            </div>
            <span className="font-space font-bold text-sm sm:text-xl">{t('vocabulary.deck.createCustom')}</span>
          </div>
        </button>

        {/* Subcategories Grid, equal height cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6 auto-rows-fr">
          {/* Official Subcategories (Green accent) */}
          {Object.entries(allTopics)
            .filter(([, topic]) => topic.parentCategory === selectedCategory)
            .map(([key, topic], index) => {
              const isLocked = subscriptionStatus?.isFree && index > 0;
              return (
              <div
                key={key}
                onClick={() => {
                  if (isLocked) {
                    setBlockedCategoryName(topic.name || key);
                    setShowPremiumModal(true);
                    trackPaywallShown({ context: 'vocabulary' });
                  } else {
                    handleSubcategorySelect(key);
                  }
                }}
                className={`vocab-subcategory-card p-3 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] text-black h-full cursor-pointer group touch-manipulation min-h-[44px] min-w-[44px] ${isLocked ? 'border-[#6366f1] bg-gradient-to-br from-white via-[#f8f5ff] to-[#eef4ff] shadow-[0_8px_24px_rgba(99,102,241,0.2)]' : 'border-black bg-white'}`}
              >
                {/* Decorative Dots (Green) */}
                <div className="vocab-sub-dot vocab-sub-dot-1 hidden sm:block"></div>
                <div className="vocab-sub-dot vocab-sub-dot-2 hidden sm:block"></div>
                <div className="vocab-sub-dot vocab-sub-dot-3 hidden sm:block"></div>

                {/* Shine Effect */}
                <div className="vocab-shine-effect"></div>

                {/* Corner Decorations */}
                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/50 transition-colors hidden sm:block"></div>
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/50 transition-colors hidden sm:block"></div>
                {isLocked && (
                  <div className="absolute top-3 right-3 z-20 inline-flex items-center rounded-full border border-[#6366f1]/30 bg-white/95 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#4f46e5]">
                    {t('premium.only')}
                  </div>
                )}

                {/* Icon */}
                <div
                  className="vocab-sub-icon w-[40px] h-[40px] sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-3xl mb-2 sm:mb-4 mx-auto"
                  style={{ backgroundColor: isLocked ? '#6366f1' : '#4f46e5' }}
                >
                  {topic.icon || '📗'}
                </div>

                {/* Content */}
                <div className="vocab-sub-content text-center">
                  <h4 className="font-space font-black text-sm sm:text-[clamp(1rem,3vw,1.25rem)] mb-1 sm:mb-2 line-clamp-2">{topic.name}</h4>
                  <p className="text-xs sm:text-sm text-black/60 group-hover:text-white/80 transition-colors">
                    {t('vocabulary.deck.cards', { count: (topic.cards || []).length })}
                  </p>
                </div>
              </div>
              );
            })}

          {/* User Custom Decks (Purple/Black) */}
          {Object.entries(allTopics)
            .filter(([, topic]) => !topic.isOfficial && !topic.parentCategory)
            .map(([key, topic]) => (
              <div key={key} className="relative group">
                <div
                  onClick={() => handleSubcategorySelect(key)}
                  className="vocab-custom-card p-3 sm:p-6 rounded-xl sm:rounded-3xl border-2 sm:border-[3px] border-black bg-black text-white cursor-pointer relative overflow-hidden touch-manipulation min-h-[44px] min-w-[44px] h-full"
                >
                  {/* Purple Glow Effect */}
                  <div className="vocab-purple-glow"></div>

                  {/* Decorative Dots (Purple) */}
                  <div className="vocab-custom-dot vocab-custom-dot-1 hidden sm:block"></div>
                  <div className="vocab-custom-dot vocab-custom-dot-2 hidden sm:block"></div>
                  <div className="vocab-custom-dot vocab-custom-dot-3 hidden sm:block"></div>

                  {/* Shine Effect */}
                  <div className="vocab-shine-effect"></div>

                  {/* Corner Decorations */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-white/20 group-hover:border-purple-400 transition-colors hidden sm:block"></div>
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-white/20 group-hover:border-purple-400 transition-colors hidden sm:block"></div>

                  {/* Icon */}
                  <div className="vocab-custom-icon w-[40px] h-[40px] sm:w-16 sm:h-16 bg-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-3xl mb-2 sm:mb-4 mx-auto">
                    {topic.icon || '✏️'}
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h4 className="font-space font-black text-sm sm:text-[clamp(1rem,3vw,1.25rem)] mb-1 sm:mb-2 line-clamp-2">{topic.name}</h4>
                    <p className="text-sm text-white/60 mb-2">
                      {t('vocabulary.deck.cards', { count: (topic.cards || []).length })}
                    </p>
                    <span className="inline-block px-3 py-1 bg-purple-600/30 border border-purple-500/50 rounded-full text-xs font-bold text-purple-300">
                      {t('vocabulary.deck.custom')}
                    </span>
                  </div>

                  {/* Edit/Delete Buttons */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all flex gap-2 z-20">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingDeck(topic);
                        setShowDeckEditor(true);
                      }}
                      className="w-10 h-10 bg-white text-black rounded-xl flex items-center justify-center hover:bg-purple-500 hover:text-white transition-all hover:scale-110 shadow-lg"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDeck(key);
                      }}
                      className="w-10 h-10 bg-white text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all hover:scale-110 shadow-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>

      </div>

      {/* Deck Editor Modal */}
      {showDeckEditor && (
        <DeckEditor
          onSave={handleSaveDeck}
          onCancel={() => {
            setShowDeckEditor(false);
            setEditingDeck(null);
          }}
          initialData={editingDeck}
        />
      )}

      {/* Vocabulary Guide Modal */}
      {showGuide && (
        <VocabularyGuide
          isOpen={showGuide}
          onClose={handleGuideComplete}
        />
      )}

      {/* Bottom Navigation. Subcategories */}
      <GrammarBottomNav buttons={[
        { id: 'back', icon: ChevronLeft, label: t('grammar.back'), onClick: () => setCurrentView('categories'), color: '#3A86FF' },
        { id: 'home', icon: Home, label: 'Home', onClick: () => navigate('/dashboard'), color: '#3A86FF' },
        { id: 'create', icon: Plus, label: t('vocabulary.deck.createCustom', { defaultValue: 'Create' }), onClick: () => setShowDeckEditor(true), active: true, color: '#3A86FF' },
        { id: 'help', icon: HelpCircle, label: 'Guide', onClick: showGuideManually, color: '#3A86FF' },
      ]} />
    </div>
  );

    case 'practice':
      return (
        <div className="bg-white relative overflow-hidden p-1.5 sm:p-4 flex flex-col" style={{
          height: '100dvh',
          paddingTop: 'max(0.25rem, env(safe-area-inset-top))',
          // Increased bottom padding on mobile to account for larger buttons (2 rows) + navigation bar
          paddingBottom: 'max(10rem, calc(env(safe-area-inset-bottom) + 10rem))',
        }}>
          {sessionComplete && <Confetti />}

          <div className="relative z-10 max-w-4xl mx-auto w-full flex-1 flex flex-col min-h-0">
            {/* Desktop header, hidden on mobile, now in bottom nav */}
            <div className="hidden sm:flex mb-8 justify-between items-center flex-shrink-0">
  <button
    onClick={() => {
      if (subscriptionStatus?.isFree) {
        setPendingExitNav(() => () => setCurrentView('subcategories'));
        setShowPremiumModal(true);
        trackPaywallShown({ context: 'vocabulary' });
      } else {
        setCurrentView('subcategories');
      }
    }}
    onMouseEnter={() => setIsBackHovered_Practice(true)}
    onMouseLeave={() => setIsBackHovered_Practice(false)}
    className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: isBackHovered_Practice ? '100%' : '0%',
        background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
        transition: 'width 0.7s ease-out',
        zIndex: 0
      }}
    />
    <ArrowLeft
      className="w-5 h-5 relative z-10 transition-colors duration-500"
      style={{ color: isBackHovered_Practice ? '#ffffff' : '#000000' }}
    />
    <span
      className="font-semibold relative z-10 transition-colors duration-500"
      style={{ color: isBackHovered_Practice ? '#ffffff' : '#000000' }}
    >
      {t("grammar.back")}
    </span>
  </button>

  <div className="flex items-center gap-3 px-6 py-3 bg-white border-[3px] border-black/15 rounded-full shadow-lg">
    <span className="text-sm font-medium text-black/60">{t('vocabulary.deck.cards')}</span>
    <span className="font-space font-black text-xl text-[#3A86FF]">{currentCardIndex + 1}</span>
    <span className="text-black/40">/</span>
    <span className="font-space font-bold text-lg text-black/60">{currentCards.length}</span>
  </div>

  <div className="flex items-center gap-3 px-6 py-3 bg-black text-white rounded-full shadow-lg">
    <BarChart3 className="w-5 h-5" />
    <span className="font-space font-bold text-lg">{sessionStats.studied}</span>
  </div>
</div>

            {/* Card Stack Area, constrained height to leave room for buttons + nav */}
            <div className="relative mb-2 sm:mb-6 flex-1 flex items-center justify-center min-h-0" style={{ 
              // On mobile: account for larger 2-row buttons (approx 160px) + nav (64px) + header
              // On desktop: original calculation
              maxHeight: 'calc(100dvh - min(450px, 350px))',
              minHeight: '300px' // Minimum height for card visibility
            }}>
              <div className="w-full max-w-2xl mx-auto h-full max-h-[600px] relative">
                <SwipeHint visible={!isCardFlipped} />

                {currentCard && (
                  <VocabularyCard
                    card={currentCard}
                    onSwipe={handleSwipe}
                    isVisible={true}
                    zIndex={1}
                    showIndicators={true}
                    enableAudio={true}
                    onFlipChange={setIsCardFlipped}
                  />
                )}
              </div>
            </div>

            {/* Review Buttons, positioned right above bottom nav with proper spacing */}
            <div 
              className="flex-shrink-0 px-2 sm:px-0" 
              style={{ 
                // Ensure buttons are above navigation bar (nav is h-16 = 64px, plus safe area)
                marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)',
                paddingBottom: '0.5rem'
              }}
            >
              <ReviewButtons
                onRate={handleCardRating}
                buttonLabels={currentCard ? srsService.getButtonLabels(currentCard) : null}
                disabled={!currentCard}
                isFlipped={isCardFlipped}
                showKeyboardHints={true}
              />
            </div>
        </div>

          {/* Bottom Navigation. Practice */}
          <GrammarBottomNav buttons={[
            { id: 'back', icon: ChevronLeft, label: t('grammar.back'), onClick: () => {
              if (subscriptionStatus?.isFree) {
                setPendingExitNav(() => () => setCurrentView('subcategories'));
                setShowPremiumModal(true);
                trackPaywallShown({ context: 'vocabulary' });
              } else {
                setCurrentView('subcategories');
              }
            }, color: '#3A86FF' },
            { id: 'progress', type: 'progress', label: 'Cards', progress: currentCards.length > 0 ? ((currentCardIndex + 1) / currentCards.length) * 100 : 0, current: currentCardIndex + 1, total: currentCards.length, color: '#3A86FF' },
            { id: 'studied', icon: BarChart3, label: `${sessionStats.studied} Studied`, onClick: () => {}, active: true, color: '#3A86FF' },
            { id: 'topics', icon: LayoutGrid, label: 'Topics', onClick: () => setCurrentView('categories'), color: '#3A86FF' },
          ]} />
              </div>
      );

    case 'stats':
      return (
        <div className="min-h-screen bg-white relative overflow-hidden p-4 pb-24 sm:pb-4 flex items-center justify-center">
          <div className="relative z-10">
          <SessionStats
            stats={sessionStats}
            onContinue={() => setCurrentView('subcategories')}
          />
        </div>

          {/* Bottom Navigation. Stats */}
          <GrammarBottomNav buttons={[
            { id: 'topics', icon: LayoutGrid, label: 'Topics', onClick: () => setCurrentView('subcategories'), color: '#3A86FF' },
            { id: 'home', icon: Home, label: 'Home', onClick: () => {
              if (subscriptionStatus?.isFree) {
                setPendingExitNav(() => () => navigate('/dashboard'));
                setShowPremiumModal(true);
                trackPaywallShown({ context: 'vocabulary' });
              } else {
                navigate('/dashboard');
              }
            }, color: '#3A86FF' },
            { id: 'practice', icon: BookOpen, label: 'Practice', onClick: () => {
              if (subscriptionStatus?.isFree) {
                setPendingExitNav(() => () => navigate('/practice'));
                setShowPremiumModal(true);
                trackPaywallShown({ context: 'vocabulary' });
              } else {
                navigate('/practice');
              }
            }, color: '#3A86FF' },
          ]} />
            </div>
      );

    default:
      return null;
  }
  })();

  return (
    <>
      {mainContent}
      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="vocabulary"
      />
    </>
  );
};

export default VocabularyPage;